<template>
	<BlockVisibilityObserver title="FAQ">
		<section class="kova-lp-faq" :class="[`kova-lp-faq_theme_${theme}`, { 'kova-lp-faq_centered': centeredHeading }]">
			<div class="kova-lp-faq__inner">
				<div class="kova-lp-faq__copy">
					<h2 class="kova-lp-faq__title">{{ title }}</h2>
					<p class="kova-lp-faq__subtitle">{{ subtitle }}</p>
				</div>

				<ul class="kova-lp-faq__list">
					<li
						v-for="(item, index) in items"
						:key="item.question"
						class="kova-lp-faq__item"
						:class="{ 'kova-lp-faq__item_sections': item.sections }"
					>
						<button
							:id="`${uid}-q${index}`"
							:aria-controls="`${uid}-a${index}`"
							:aria-expanded="open === index"
							class="kova-lp-faq__question"
							type="button"
							@click="toggle(index)"
						>
							<span>{{ item.question }}</span>
							<InlineSvg
								class="kova-lp-faq__chevron"
								:class="{ 'kova-lp-faq__chevron_open': open === index }"
								:src="chevron"
							/>
						</button>

						<div
							v-show="open === index"
							:id="`${uid}-a${index}`"
							:aria-labelledby="`${uid}-q${index}`"
							class="kova-lp-faq__answer"
							role="region"
						>
							<p v-for="(paragraph, i) in item.answer" :key="i" class="kova-lp-faq__text">
								{{ paragraph }}
							</p>
							<p v-if="item.note" class="kova-lp-faq__note">{{ item.note }}</p>

							<div v-for="section in item.sections" :key="section.title" class="kova-lp-faq__section">
								<p class="kova-lp-faq__section-title">{{ section.title }}</p>
								<p class="kova-lp-faq__text">{{ section.text }}</p>
							</div>
						</div>
					</li>
				</ul>
			</div>
		</section>
	</BlockVisibilityObserver>
</template>

<script>
import { useId } from 'vue'
import BlockVisibilityObserver from '@/components/BlockVisibilityObserver.vue'
import analytics from '@/modules/analytics'
import { stepEvents } from '@/composables/useOnboardingStepEvents'
import chevron from '@/assets/kova/landing/faq-chevron.svg'

export default {
	name: 'LandingKovaFaq',

	components: { BlockVisibilityObserver },

	props: {
		theme: {
			type: String,
			default: 'default',
			validator: (value) => ['default', 'v2'].includes(value),
		},

		// Centre the heading on mobile too, as the USA frame does (6346:46695); desktop
		// always centres it.
		centeredHeading: {
			type: Boolean,
			default: false,
		},

		// [{ question, answer: string[], sections?: [{ title, text }] }]
		items: {
			type: Array,
			required: true,
		},

		subtitle: {
			type: String,
			required: true,
		},

		title: {
			type: String,
			required: true,
		},
	},

	setup() {
		return { chevron, uid: useId() }
	},

	data() {
		return {
			// Figma draws every item expanded to show the copy; shipping collapsed is
			// the usual reading of that, so nothing is open until asked.
			open: null,
		}
	},

	methods: {
		toggle(index) {
			const isOpening = this.open !== index
			this.open = isOpening ? index : null

			// Only opening is a signal; collapsing again is noise. Rides the existing
			// OnboardingButtonClick schema, so no new event is needed.
			if (isOpening) {
				analytics.track('OnboardingButtonClick', {
					...(stepEvents.currentStepId && { onboarding_step_id: stepEvents.currentStepId }),
					...(stepEvents.currentTitle && { onboarding_title: stepEvents.currentTitle }),
					block_name: 'FAQ',
					button_name: this.items[index].question,
				})
			}
		},
	},
}
</script>

<style lang="scss" scoped>
// Mobile (Figma 4843:27139).
.kova-lp-faq {
	font-family: var(--kova-lp-font);
	background: var(--kova-lp-page);
}

.kova-lp-faq__inner {
	display: flex;
	flex-direction: column;
	gap: var(--size-24);
	padding: var(--size-40) var(--size-20) 0;
}

.kova-lp-faq__copy {
	display: flex;
	flex-direction: column;
	gap: var(--size-12);
}

.kova-lp-faq__title {
	margin: 0;

	font-size: var(--size-32);
	font-weight: 500;
	line-height: 1.12;
	letter-spacing: -0.64px;

	color: var(--kova-lp-text);
}

.kova-lp-faq_centered .kova-lp-faq__copy {
	text-align: center;
}

.kova-lp-faq__subtitle {
	margin: 0;

	font-size: var(--size-16);
	line-height: 1.5;

	color: var(--kova-lp-faq-subtitle);
}

.kova-lp-faq__list {
	display: flex;
	flex-direction: column;
	gap: var(--size-8);
	margin: 0;
	padding: 0;

	list-style: none;
}

.kova-lp-faq__item {
	box-sizing: border-box;
	display: flex;
	flex-direction: column;
	gap: var(--size-8);
	padding: var(--size-20);

	border-radius: var(--size-20);
	background: var(--kova-lp-surface);
}

.kova-lp-faq__question {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: var(--size-8);
	width: 100%;
	padding: 0;

	font-family: inherit;
	font-size: var(--size-18);
	font-weight: 500;
	line-height: var(--size-24);

	border: none;
	color: var(--kova-lp-text);
	background: none;
	cursor: pointer;
	text-align: left;
}

.kova-lp-faq__chevron {
	flex-shrink: 0;

	color: var(--kova-lp-faq-icon);
	transition: transform 0.2s ease;
}

.kova-lp-faq__chevron :deep(svg) {
	display: block;
	width: 16.6px; // the vector inside Figma's 24px icon slot
	height: auto;
}

.kova-lp-faq__chevron_open {
	transform: rotate(180deg);
}

.kova-lp-faq__answer {
	display: flex;
	flex-direction: column;
	gap: var(--size-24);
}

.kova-lp-faq__text {
	margin: 0;

	font-size: var(--size-14);
	line-height: 1.4;

	color: var(--kova-lp-text);
}

.kova-lp-faq__section {
	display: flex;
	flex-direction: column;
	gap: var(--size-8);
}

.kova-lp-faq__section-title {
	margin: 0;

	font-size: var(--size-14);
	font-weight: 500;
	line-height: var(--size-16);

	color: var(--kova-lp-text);
}

// Desktop (Figma 5026:7325): the copy centres and everything scales up.
@media (min-width: 1024px) {
	// 1120px of content between Figma's 160px gutters.
	.kova-lp-faq__inner {
		box-sizing: border-box;
		max-width: 1200px;
		margin-inline: auto;
		padding: 104px var(--size-40) 0; // 104 top — section padding, off the ladder
		gap: var(--size-40);
	}

	.kova-lp-faq__copy {
		align-items: center;
		gap: var(--size-24);

		text-align: center;
	}

	.kova-lp-faq__title {
		font-size: 62px; // 5026:7332 — between --size-60 and --size-64, kept exact
		line-height: 1.2;
		letter-spacing: -1.24px;
	}

	.kova-lp-faq__subtitle {
		font-size: var(--size-20);
	}

	.kova-lp-faq__list {
		gap: var(--size-16);
	}

	.kova-lp-faq__item {
		gap: var(--size-32);
		padding: var(--size-44);

		border-radius: var(--size-36);
	}

	.kova-lp-faq__question {
		font-size: var(--size-34);
		line-height: 1;
	}

	.kova-lp-faq__chevron :deep(svg) {
		width: 23.5px; // the vector inside the 34px icon slot
	}

	.kova-lp-faq__answer {
		gap: var(--size-32);
	}

	.kova-lp-faq__text {
		font-size: var(--size-24);
		line-height: 1.5;
	}

	.kova-lp-faq__section {
		gap: var(--size-8);
	}

	.kova-lp-faq__section-title {
		font-size: var(--size-28);
		line-height: 1.5;
	}
}
.kova-lp-faq__note {
	margin: 0;
	font-size: var(--size-12);
	line-height: var(--size-16);
	color: var(--kova-lp-muted);
}

// DEV-35932, Figma 6420:54157 / 6557:32813: the same accordion with v2 spacing and colours.
.kova-lp-faq_theme_v2 {
	.kova-lp-faq__inner {
		padding: var(--size-20) var(--size-16) var(--size-32);
	}
	.kova-lp-faq__copy {
		text-align: center;
	}
	.kova-lp-faq__title,
	.kova-lp-faq__question,
	.kova-lp-faq__text {
		color: var(--kova-lp-ink);
	}
	.kova-lp-faq__title {
		line-height: var(--size-36);
	}
	.kova-lp-faq__subtitle {
		color: var(--kova-lp-text-soft);
	}
	.kova-lp-faq__chevron {
		width: 16.6px;
		height: auto;
	}
	.kova-lp-faq__chevron_open {
		transform: scaleY(-1);
	}
	.kova-lp-faq__answer {
		gap: var(--size-8);
	}
	.kova-lp-faq__item_sections {
		gap: var(--size-16);
		.kova-lp-faq__answer {
			gap: var(--size-16);
		}
	}
	.kova-lp-faq__text {
		line-height: var(--size-20);
	}
	.kova-lp-faq__section-title {
		font-size: var(--size-16);
	}
}

@media (min-width: 1024px) {
	.kova-lp-faq__note {
		font-size: var(--size-16);
		line-height: var(--size-22);
	}
	.kova-lp-faq_theme_v2 {
		.kova-lp-faq__inner {
			padding: var(--size-60) var(--size-40);
		}
		.kova-lp-faq__title {
			line-height: var(--size-72);
		}
		.kova-lp-faq__subtitle {
			color: var(--kova-lp-ink);
		}
		.kova-lp-faq__chevron {
			width: 23.5px;
		}
		.kova-lp-faq__item_sections {
			gap: var(--size-32);
		}
		.kova-lp-faq__answer,
		.kova-lp-faq__item_sections .kova-lp-faq__answer {
			gap: var(--size-32);
		}
		.kova-lp-faq__text {
			line-height: var(--size-36);
		}
		.kova-lp-faq__section-title {
			font-size: var(--size-28);
			line-height: var(--size-42);
			color: var(--kova-lp-ink);
		}
	}
}

@media (prefers-reduced-motion: reduce) {
	.kova-lp-faq_theme_v2 .kova-lp-faq__chevron {
		transition: none;
	}
}
</style>
